/**
 * ESCALA OFICIAL DE CAMADAS (z-index) DO SISTEMA
 * ------------------------------------------------------------------
 *  40   headers/sticky da aplicação
 *  50   overlay + conteúdo de Dialog / Sheet / AlertDialog (raiz)
 *  60   banners globais (ex.: sessão de suporte WL)
 *  70   Dialog "elevado" (legado — className explícito z-[70])
 *  80   Dialog aninhado nível 1 (aberto de dentro de outro Dialog)
 *  90   Dialog aninhado nível 2+
 * 120   camada flutuante: Select, Popover, Dropdown, Command,
 *       Context menu, Hover card (SEMPRE acima de qualquer modal)
 * 130   Tooltip
 * 140   Toast
 *
 * Regra: nunca inventar números novos. Conteúdo flutuante deve ficar
 * acima de qualquer modal — por isso os componentes base já usam 120+.
 *
 * Compartilham esta MESMA escala: Dialog, Sheet, AlertDialog e Drawer — todos
 * participam do DialogDepthContext e sobem de camada automaticamente ao serem
 * abertos de dentro de outro deles.
 *
 * CHANGELOG:
 *   RELEASE: 2026-09-17 v2.2.0 - AlertDialog e Drawer passam a usar a mesma escala
 *            (corrige caixas de confirmação abrindo atrás de modais aninhados).
 *   RELEASE: 2026-09-17 v2.1.0 - Exporta DialogDepthContext e zClassForDepth para que o
 *            Sheet use a mesma escala de camadas (corrige sheet abrindo atrás de modal
 *            aninhado, ex.: detalhes do contrato dentro do modal de negócio).
 *   RELEASE: 2026-08-21 v2.0.0 - Empilhamento automático de modais aninhados
 *            via DialogDepthContext (corrige modais de criação abrindo por trás
 *            do modal "Nova Tarefa", que usa z-[70]).
 */
import * as React from "react";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSwipeToClose } from "@/hooks/useSwipeToClose";

/**
 * Contexto de profundidade de empilhamento dos modais.
 * Cada <Dialog> renderizado DENTRO de outro <Dialog> incrementa a profundidade,
 * fazendo overlay e conteúdo subirem de camada automaticamente.
 */
const DialogDepthContext = React.createContext(0);

/** Mapa profundidade -> classes de z-index (overlay e conteúdo na mesma camada) */
const DEPTH_Z_CLASS: Record<number, string> = {
  0: "z-50",
  1: "z-[80]",
  2: "z-[90]",
};

/** Resolve a classe de z-index para uma profundidade (satura em 90) */
function zClassForDepth(depth: number): string {
  return DEPTH_Z_CLASS[Math.min(depth, 2)] ?? DEPTH_Z_CLASS[2];
}

/**
 * Dialog raiz — envolve os filhos com a profundidade incrementada, de modo que
 * qualquer Dialog aninhado saiba que deve ficar acima do modal que o abriu.
 */
const Dialog = ({ children, ...props }: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Root>) => {
  const parentDepth = React.useContext(DialogDepthContext);
  return (
    <DialogPrimitive.Root {...props}>
      <DialogDepthContext.Provider value={parentDepth + 1}>{children}</DialogDepthContext.Provider>
    </DialogPrimitive.Root>
  );
};
Dialog.displayName = "Dialog";

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => {
  // Profundidade do PRÓPRIO dialog = valor do provider - 1
  const depth = Math.max(React.useContext(DialogDepthContext) - 1, 0);
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
        "fixed inset-0 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        zClassForDepth(depth),
        className,
      )}
      {...props}
    />
  );
});
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;


interface DialogContentProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  preventCloseOnOutsideInteraction?: boolean;
  hideCloseButton?: boolean;
  /**
   * Habilita o gesto "arrastar para baixo para fechar" no mobile.
   * - `true` (padrão): ativo apenas em viewports mobile, descartando o modal
   *   quando o usuário puxa o conteúdo para baixo (similar a sheets nativos).
   * - `false`: desativa o gesto (use para modais pequenos/críticos onde o
   *   descarte acidental seria ruim).
   */
  enableSwipeToClose?: boolean;
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, preventCloseOnOutsideInteraction = false, hideCloseButton = false, enableSwipeToClose = true, ...props }, ref) => {
  const isMobile = useIsMobile();
  // Profundidade do PRÓPRIO dialog = valor do provider - 1
  const depth = Math.max(React.useContext(DialogDepthContext) - 1, 0);
  // Gesto só faz sentido em mobile e quando o caller permite
  const swipeEnabled = enableSwipeToClose && isMobile;


  // Botão Close invisível disparado pelo gesto — delega o fechamento ao Radix
  // (que respeita onOpenChange do Dialog raiz, sem prop drilling).
  const closeProxyRef = React.useRef<HTMLButtonElement | null>(null);
  const handleSwipeClose = React.useCallback(() => {
    closeProxyRef.current?.click();
  }, []);

  const { contentRef, contentStyle, handlers, isDragging } = useSwipeToClose({
    enabled: swipeEnabled,
    onClose: handleSwipeClose,
  });

  const preventHandlers = preventCloseOnOutsideInteraction ? {
    onInteractOutside: (e: Event) => e.preventDefault(),
    onPointerDownOutside: (e: Event) => e.preventDefault(),
    onFocusOutside: (e: Event) => e.preventDefault(),
  } : {};

  // Compose ref externo + ref interno do hook
  const composedRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      contentRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
    },
    [ref, contentRef],
  );

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={composedRef}
        className={cn(
          "fixed left-[50%] top-[50%] grid w-full max-w-lg translate-x-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
          zClassForDepth(depth),
          // Centralização vertical: com swipe habilitado, compomos -50% +
          // deslocamento do arrasto via CSS var, preservando a posição
          // inicial e as animações do Radix.
          swipeEnabled
            ? "translate-y-[calc(-50%+var(--swipe-y,0px))]"
            : "translate-y-[-50%]",
          className,
        )}
        style={swipeEnabled ? contentStyle : undefined}
        {...(swipeEnabled ? handlers : {})}
        {...preventHandlers}
        {...props}
      >
        {/* Drag handle visual — sinaliza ao usuário no mobile que o modal pode
            ser arrastado para baixo para fechar. Renderizado apenas quando o
            gesto está habilitado e visível somente no mobile. */}
        {swipeEnabled && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute left-1/2 top-1.5 z-10 h-1 w-9 -translate-x-1/2 rounded-full bg-muted-foreground/30"
          />
        )}
        {children}
        {!hideCloseButton && (
          <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity data-[state=open]:bg-accent data-[state=open]:text-muted-foreground hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
        {/* Close proxy invisível usado pelo swipe-to-close para acionar o
            fluxo padrão de fechamento do Radix (respeita onOpenChange). */}
        {swipeEnabled && (
          <DialogPrimitive.Close
            ref={closeProxyRef}
            aria-hidden="true"
            tabIndex={-1}
            className="sr-only"
          />
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)} {...props} />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)} {...props} />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  // v2.1.0: expostos para que o Sheet participe da MESMA escala de camadas
  DialogDepthContext,
  zClassForDepth,
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
